import type {
  DocumentTypeDescriptor,
  DocumentTypeRef,
  FilterExpression,
  QueryDiagnostic,
  QueryMaterializationDiagnostic,
  QueryRelationDescriptor,
  QuerySessionState,
} from '@aeppic/query/contracts'

import type { Query } from '@aeppic/query'
import { QueryMatcher, Scopes } from '../../model/query/index.js'
import { compileSessionExpressions } from './compiler.js'
import { describeDocumentTypes } from './metadata.js'
import type { QueryToolbarAeppicApi } from './types.js'

interface MaterializeResult {
  session: QuerySessionState
  candidateQuery: Query
  diagnostics: QueryDiagnostic[]
  materializations: QueryMaterializationDiagnostic[]
}

interface ExpressionResult {
  expression?: FilterExpression
  diagnostics: QueryDiagnostic[]
  materializations: QueryMaterializationDiagnostic[]
}

export async function materializeSessionRelations(
  aeppic: QueryToolbarAeppicApi,
  session: QuerySessionState,
  descriptors: readonly DocumentTypeDescriptor[],
  limit: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<MaterializeResult> {
  const entries: Array<{ key: 'search' | 'toolbarFilter' | string, expression: FilterExpression | null, path: readonly (string | number)[] }> = [
    { key: 'search', expression: session.search, path: ['search'] },
    { key: 'toolbarFilter', expression: session.toolbarFilter, path: ['toolbarFilter'] },
    ...Object.keys(session.contributions).sort().map(key => ({ key, expression: session.contributions[key], path: ['contributions', key] })),
  ]
  const next = { ...session, contributions: { ...session.contributions } }
  const diagnostics: QueryDiagnostic[] = []
  const materializations: QueryMaterializationDiagnostic[] = []
  const candidate = compileSessionExpressions(withoutRelations(session), descriptors)
  diagnostics.push(...candidate.diagnostics)
  const candidateQuery = candidate.query ?? session.baseQuery

  for (const entry of entries) {
    if (!entry.expression) continue
    const result = await materializeExpression(aeppic, entry.expression, descriptors, candidateQuery, limit, pageSize, entry.path, signal)
    diagnostics.push(...result.diagnostics)
    materializations.push(...result.materializations)
    if (!result.expression) continue
    if (entry.key === 'search') next.search = result.expression
    else if (entry.key === 'toolbarFilter') next.toolbarFilter = result.expression
    else next.contributions[entry.key] = result.expression
  }

  return { session: next, candidateQuery, diagnostics, materializations }
}

export function compileSessionWithoutRelations(
  session: QuerySessionState,
  descriptors: readonly DocumentTypeDescriptor[],
) {
  return compileSessionExpressions(withoutRelations(session), descriptors)
}

export function sessionContainsRelations(session: QuerySessionState): boolean {
  return containsRelation(session.search)
    || containsRelation(session.toolbarFilter)
    || Object.values(session.contributions).some(containsRelation)
}

async function materializeExpression(
  aeppic: QueryToolbarAeppicApi,
  expression: FilterExpression,
  descriptors: readonly DocumentTypeDescriptor[],
  candidateQuery: Query,
  limit: number,
  pageSize: number,
  path: readonly (string | number)[],
  signal?: AbortSignal,
): Promise<ExpressionResult> {
  throwIfAborted(signal)
  if (expression.kind === 'group') {
    const children: FilterExpression[] = []
    const diagnostics: QueryDiagnostic[] = []
    const materializations: QueryMaterializationDiagnostic[] = []
    for (let index = 0; index < expression.children.length; index++) {
      const result = await materializeExpression(aeppic, expression.children[index], descriptors, candidateQuery, limit, pageSize, [...path, 'children', index], signal)
      diagnostics.push(...result.diagnostics)
      materializations.push(...result.materializations)
      if (result.expression) children.push(result.expression)
    }
    return { expression: { ...expression, children }, diagnostics, materializations }
  }

  if (expression.kind !== 'relation') {
    return { expression, diagnostics: [], materializations: [] }
  }

  const relation = findRelation(descriptors, expression.relationId)
  if (!relation) {
    return failure('unknown-relation', `Unknown query relation ${expression.relationId}`, [...path, 'relationId'])
  }
  const targetTypes = expression.targetTypes?.length ? expression.targetTypes : relation.targetTypes
  if (targetTypes.length === 0) {
    return failure('missing-relation-target-type', `Relation ${expression.relationId} has no target type`, path)
  }

  const targetDescriptors = await describeDocumentTypes(aeppic, targetTypes)
  const targetSession: QuerySessionState = {
    baseQuery: targetBaseQuery(targetTypes),
    documentTypes: targetTypes,
    search: null,
    toolbarFilter: expression.expression,
    contributions: {},
  }
  const nested = await materializeSessionRelations(aeppic, targetSession, targetDescriptors, limit, pageSize, signal)
  const compiled = compileSessionExpressions(nested.session, targetDescriptors)
  const diagnostics = [...nested.diagnostics, ...compiled.diagnostics]
  if (!compiled.query || diagnostics.some(diagnostic => diagnostic.severity === 'error')) {
    return { diagnostics, materializations: nested.materializations }
  }

  const ids = relation.kind === 'reference' && relation.fieldPath
    ? await matchingTargetIds(aeppic, compiled.query, expression.relationId, limit, path, signal)
    : await matchingRootIds(aeppic, candidateQuery, compiled.query, relation, limit, pageSize, path, signal)
  if ('diagnostic' in ids) {
    return {
      diagnostics: [...diagnostics, ids.diagnostic],
      materializations: [...nested.materializations, { relationId: expression.relationId, matchedIds: limit, complete: false, limit }],
    }
  }

  return {
    expression: {
      kind: 'field',
      field: relation.kind === 'reference' && relation.fieldPath ? `${relation.fieldPath}.id` : 'id',
      operator: 'is_one_of',
      value: ids.values,
    },
    diagnostics,
    materializations: [...nested.materializations, { relationId: expression.relationId, matchedIds: ids.values.length, complete: true, limit }],
  }
}

async function matchingTargetIds(aeppic: QueryToolbarAeppicApi, query: Query, relationId: string, limit: number, path: readonly (string | number)[], signal?: AbortSignal) {
  const targets = await aeppic.find(query, { start: 0, size: limit + 1, fields: [] })
  throwIfAborted(signal)
  if (targets.length > limit) {
    return { diagnostic: { code: 'relation-materialization-limit', message: `Relation ${relationId} matched more than ${limit} target documents`, severity: 'error' as const, path } }
  }
  return { values: [...new Set(targets.map(target => target.id).filter(Boolean))] }
}

async function matchingRootIds(
  aeppic: QueryToolbarAeppicApi,
  candidateQuery: Query,
  targetQuery: Query,
  relation: QueryRelationDescriptor,
  limit: number,
  pageSize: number,
  path: readonly (string | number)[],
  signal?: AbortSignal,
) {
  if (!relation.resolvePath) {
    return { diagnostic: { code: 'missing-relation-resolve-path', message: `Named relation ${relation.id} has no resolve path`, severity: 'error' as const, path } }
  }

  const matcher = new QueryMatcher(targetQuery)
  const matchingIds = new Set<string>()
  let scanned = 0
  let start = 0

  while (true) {
    const roots = await aeppic.find(candidateQuery, { start, size: pageSize, sort: 'id', fields: [] })
    throwIfAborted(signal)
    for (const root of roots) {
      scanned += 1
      if (scanned > limit) {
        return { diagnostic: { code: 'relation-materialization-limit', message: `Relation ${relation.id} requires scanning more than ${limit} root documents`, severity: 'error' as const, path } }
      }
      const targets = await aeppic.resolveAll(root.id, relation.resolvePath)
      throwIfAborted(signal)
      if (targets.some(target => matcher.match(target))) matchingIds.add(root.id)
    }
    if (roots.length < pageSize) break
    start += roots.length
  }

  return { values: [...matchingIds] }
}

function targetBaseQuery(targetTypes: readonly DocumentTypeRef[]) {
  return Scopes.global().forms([...new Set(targetTypes.map(type => type.id))]).toQuery()
}

function findRelation(descriptors: readonly DocumentTypeDescriptor[], id: string): QueryRelationDescriptor | undefined {
  return descriptors.flatMap(descriptor => descriptor.relations).find(relation => relation.id === id)
}

function withoutRelations(session: QuerySessionState): QuerySessionState {
  return {
    ...session,
    search: omitRelations(session.search),
    toolbarFilter: omitRelations(session.toolbarFilter),
    contributions: Object.fromEntries(Object.entries(session.contributions).map(([key, expression]) => [key, omitRelations(expression)])),
  }
}

function omitRelations(expression: FilterExpression | null): FilterExpression | null {
  if (!expression) return null
  if (expression.kind === 'relation') return null
  if (expression.kind !== 'group') return expression

  const children = expression.children.map(omitRelations)
  if (expression.operator === 'OR' && children.some(child => child === null)) return null
  const present = children.filter((child): child is FilterExpression => child !== null)
  if (present.length === 0) return null
  return present.length === 1 ? present[0] : { ...expression, children: present }
}

function containsRelation(expression: FilterExpression | null): boolean {
  if (!expression) return false
  if (expression.kind === 'relation') return true
  if (expression.kind !== 'group') return false
  return expression.children.some(containsRelation)
}

function failure(code: string, message: string, path: readonly (string | number)[]): ExpressionResult {
  return { diagnostics: [{ code, message, severity: 'error', path }], materializations: [] }
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new Error('Query toolbar operation aborted')
}
